import React, { useState, useRef, useEffect, useCallback } from 'react';

// Editable bounding box overlay for sheet auto-detect/crop step
// Props:
//   image: HTMLImageElement (already loaded)
//   boxes: [{id,x,y,w,h}]
//   onChange: fn(newBoxes)
//   selectedId, onSelect
export default function BoxEditor({ image, boxes, onChange, selectedId, onSelect }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [dragState, setDragState] = useState(null);

  useEffect(() => {
    function updateScale() {
      if (!containerRef.current || !image) return;
      const cw = containerRef.current.clientWidth;
      const s = cw / image.naturalWidth;
      setScale(s);
    }
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [image]);

  const W = image ? image.naturalWidth : 0;
  const H = image ? image.naturalHeight : 0;
  const dispW = W * scale;
  const dispH = H * scale;

  const toImg = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(W, (clientX - rect.left) / scale)),
      y: Math.max(0, Math.min(H, (clientY - rect.top) / scale)),
    };
  }, [scale, W, H]);

  function onPointerDown(e) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    const pt = toImg(e.clientX, e.clientY);
    // Check if hitting a handle of selected box first
    if (selectedId) {
      const b = boxes.find(b => b.id === selectedId);
      if (b) {
        const handleHit = hitHandle(b, pt, scale);
        if (handleHit) {
          setDragState({ mode: 'resize', handle: handleHit, startPt: pt, startBox: { ...b }, id: b.id });
          return;
        }
      }
    }
    // Check if hitting any box
    for (let i = boxes.length - 1; i >= 0; i--) {
      const b = boxes[i];
      if (pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h) {
        onSelect(b.id);
        setDragState({ mode: 'move', startPt: pt, startBox: { ...b }, id: b.id });
        return;
      }
    }
    // Empty area: start drawing a new box
    onSelect(null);
    setDragState({ mode: 'draw', startPt: pt });
  }

  function onPointerMove(e) {
    if (!dragState) return;
    const pt = toImg(e.clientX, e.clientY);
    if (dragState.mode === 'move') {
      const dx = pt.x - dragState.startPt.x;
      const dy = pt.y - dragState.startPt.y;
      const nx = Math.max(0, Math.min(W - dragState.startBox.w, dragState.startBox.x + dx));
      const ny = Math.max(0, Math.min(H - dragState.startBox.h, dragState.startBox.y + dy));
      onChange(boxes.map(b => b.id === dragState.id ? { ...b, x: nx, y: ny } : b));
    } else if (dragState.mode === 'resize') {
      const { handle, startBox } = dragState;
      let { x, y, w, h } = startBox;
      const dx = pt.x - dragState.startPt.x;
      const dy = pt.y - dragState.startPt.y;
      if (handle.includes('e')) w = Math.max(8, startBox.w + dx);
      if (handle.includes('s')) h = Math.max(8, startBox.h + dy);
      if (handle.includes('w')) { x = startBox.x + dx; w = startBox.w - dx; if (w < 8) { x = startBox.x + startBox.w - 8; w = 8; } }
      if (handle.includes('n')) { y = startBox.y + dy; h = startBox.h - dy; if (h < 8) { y = startBox.y + startBox.h - 8; h = 8; } }
      x = Math.max(0, x); y = Math.max(0, y);
      w = Math.min(W - x, w); h = Math.min(H - y, h);
      onChange(boxes.map(b => b.id === dragState.id ? { ...b, x, y, w, h } : b));
    } else if (dragState.mode === 'draw') {
      setDragState(prev => ({ ...prev, endPt: pt }));
    }
  }

  function onPointerUp() {
    if (!dragState) return;
    if (dragState.mode === 'draw' && dragState.endPt) {
      const { startPt, endPt } = dragState;
      const x = Math.min(startPt.x, endPt.x);
      const y = Math.min(startPt.y, endPt.y);
      const w = Math.abs(endPt.x - startPt.x);
      const h = Math.abs(endPt.y - startPt.y);
      if (w > 10 && h > 10) {
        const id = 'b_' + Math.random().toString(36).slice(2, 9);
        const newBox = { id, x, y, w, h };
        onChange([...boxes, newBox]);
        onSelect(id);
      }
    }
    setDragState(null);
  }

  // Render: a wrapper div sized to fit, with image as background and overlay svg for boxes
  if (!image) return null;

  // Render draft box if drawing
  let draftRect = null;
  if (dragState && dragState.mode === 'draw' && dragState.endPt) {
    const { startPt, endPt } = dragState;
    draftRect = {
      x: Math.min(startPt.x, endPt.x),
      y: Math.min(startPt.y, endPt.y),
      w: Math.abs(endPt.x - startPt.x),
      h: Math.abs(endPt.y - startPt.y),
    };
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none"
      style={{ aspectRatio: `${W} / ${H}`, cursor: dragState ? 'crosshair' : 'default', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
      data-testid="box-editor"
    >
      <img src={image.src} alt="sheet" className="w-full h-full block pointer-events-none" draggable={false} />
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
      >
        {boxes.map((b, i) => {
          const selected = b.id === selectedId;
          return (
            <g key={b.id}>
              <rect
                x={b.x} y={b.y} width={b.w} height={b.h}
                fill="none"
                stroke={selected ? '#1f3d2b' : '#f0b430'}
                strokeWidth={selected ? 6 / scale : 4 / scale}
                strokeDasharray={selected ? 'none' : `${10 / scale} ${6 / scale}`}
              />
              <rect
                x={b.x} y={b.y} width={b.w} height={b.h}
                fill={selected ? 'rgba(31,61,43,0.18)' : 'rgba(240,180,48,0.10)'}
              />
              {/* Index label */}
              <g>
                <rect x={b.x + 4 / scale} y={b.y + 4 / scale} width={64 / scale} height={28 / scale} fill="#1f3d2b" />
                <text
                  x={b.x + 36 / scale}
                  y={b.y + 24 / scale}
                  fill="#f0b430"
                  fontSize={18 / scale}
                  fontFamily="'JetBrains Mono', monospace"
                  fontWeight={700}
                  textAnchor="middle"
                >{`#${i + 1}`}</text>
              </g>
              {selected && handles(b).map(h => (
                <rect key={h.k} x={h.x - 8 / scale} y={h.y - 8 / scale} width={16 / scale} height={16 / scale}
                      fill="#1f3d2b" stroke="#f0b430" strokeWidth={2 / scale} />
              ))}
            </g>
          );
        })}
        {draftRect && (
          <rect x={draftRect.x} y={draftRect.y} width={draftRect.w} height={draftRect.h}
                fill="rgba(208,74,58,0.18)" stroke="#d04a3a" strokeWidth={4 / scale} strokeDasharray={`${10 / scale} ${6 / scale}`} />
        )}
      </svg>
    </div>
  );
}

function handles(b) {
  return [
    { k: 'nw', x: b.x,         y: b.y },
    { k: 'n',  x: b.x + b.w/2, y: b.y },
    { k: 'ne', x: b.x + b.w,   y: b.y },
    { k: 'e',  x: b.x + b.w,   y: b.y + b.h/2 },
    { k: 'se', x: b.x + b.w,   y: b.y + b.h },
    { k: 's',  x: b.x + b.w/2, y: b.y + b.h },
    { k: 'sw', x: b.x,         y: b.y + b.h },
    { k: 'w',  x: b.x,         y: b.y + b.h/2 },
  ];
}

function hitHandle(b, pt, scale) {
  const tol = 14 / scale;
  for (const h of handles(b)) {
    if (Math.abs(pt.x - h.x) <= tol && Math.abs(pt.y - h.y) <= tol) return h.k;
  }
  return null;
}
